import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTokens } from '../../styles/tokens';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Navbar } from '../common/Navbar';
import { DonateModal } from '../common/DonateModal';
import { useStats } from '../../hooks/useStats';
import { useCountUp } from '../../hooks/useCountUp';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span<{ color: string }>`
  font-size: 14px;
  color: ${p => p.color};
  font-weight: 500;
`;

const Hero = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
`;

const Title = styled.h1<{ color: string }>`
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 900;
  color: ${p => p.color};
  margin-bottom: 16px;
  line-height: 1.1;
`;

const Subtitle = styled.p<{ color: string }>`
  font-size: 18px;
  color: ${p => p.color};
  max-width: 500px;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 36px;
`;

const StatPill = styled.div<{ surface: string; border: string; color: string }>`
  background: ${p => p.surface};
  border: 1px solid ${p => p.border};
  color: ${p => p.color};
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const StatNumber = styled.span<{ accent: string }>`
  color: ${p => p.accent};
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

const NameForm = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 380px;
  margin-bottom: 40px;
`;

const CardRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 56px;
`;

const InfoCard = styled.div<{ surface: string; border: string }>`
  background: ${p => p.surface};
  border: 1px solid ${p => p.border};
  border-radius: 14px;
  padding: 18px 20px;
  min-width: 160px;
  max-width: 190px;
  text-align: center;
`;

const CardIcon = styled.div`
  font-size: 28px;
  margin-bottom: 8px;
`;

const CardTitle = styled.div<{ color: string }>`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.color};
  margin-bottom: 4px;
`;

const CardDesc = styled.div<{ color: string }>`
  font-size: 12px;
  color: ${p => p.color};
  line-height: 1.5;
`;

const AdCard = styled.a`
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  border: none;
  border-radius: 14px;
  padding: 18px 20px;
  min-width: 160px;
  max-width: 190px;
  text-align: center;
  cursor: pointer;
  text-decoration: none;
  display: block;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35);
  }
`;

const DonateCard = styled.button`
  background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%);
  border: none;
  border-radius: 14px;
  padding: 18px 20px;
  min-width: 160px;
  max-width: 190px;
  text-align: center;
  cursor: pointer;
  display: block;
  font-family: inherit;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(239, 68, 68, 0.35);
  }
`;

const WhatsAppFloat = styled.a`
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #25D366;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  text-decoration: none;
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.5);
  z-index: 1000;
  transition: transform 0.15s, box-shadow 0.15s;
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 24px rgba(37, 211, 102, 0.65);
  }
`;

export const HomePage: React.FC = () => {
  const { user, signIn, signOut } = useAuth();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const tok = getTokens(theme);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [donateOpen, setDonateOpen] = useState(false);
  const stats = useStats();
  const animatedUsers = useCountUp(stats.users, 500);
  const animatedRooms = useCountUp(stats.rooms, 500);
  const fmt = new Intl.NumberFormat();

  const handleStart = () => {
    if (!name.trim()) return;
    signIn(name);
    navigate('/lobby');
  };

  const cards = [
    { icon: '⚡', title: t.card1Title, desc: t.card1Desc },
    { icon: '💚', title: t.card2Title, desc: t.card2Desc },
    { icon: '🏠', title: t.card3Title, desc: t.card3Desc },
    { icon: '📊', title: t.card4Title, desc: t.card4Desc },
  ];

  return (
    <Wrapper>
      <Navbar
        tokens={tok}
        onLogoClick={() => navigate('/')}
        rightContent={user && (
          <>
            <UserName color={tok.text}>{user.name}</UserName>
            <Button tokens={tok} variant="ghost" size="sm" onClick={signOut}>{t.signOut}</Button>
          </>
        )}
      />

      <Hero>
        <Title color={tok.text}>
          {t.heroTitle1}<br />
          <span style={{ color: tok.primary }}>{t.heroTitle2}</span>
        </Title>
        <Subtitle color={tok.textMuted}>
          {t.heroSubtitle}
        </Subtitle>

        {stats.loaded && (
          <StatsRow>
            <StatPill surface={tok.bgSurface} border={tok.border} color={tok.textMuted}>
              👥 <StatNumber accent={tok.primary}>{fmt.format(animatedUsers)}</StatNumber> {t.statsUsersLabel}
            </StatPill>
            <StatPill surface={tok.bgSurface} border={tok.border} color={tok.textMuted}>
              🏠 <StatNumber accent={tok.primary}>{fmt.format(animatedRooms)}</StatNumber> {t.statsRoomsLabel}
            </StatPill>
          </StatsRow>
        )}

        {user ? (
          <Button tokens={tok} size="lg" style={{ padding: '18px 40px', fontSize: '18px' }} onClick={() => navigate('/lobby')}>
            {t.createRoomNow}
          </Button>
        ) : (
          <NameForm>
            <Input
              tokens={tok}
              placeholder={t.namePlaceholder}
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleStart()}
              style={{ flex: 1 }}
            />
            <Button tokens={tok} size="lg" style={{ padding: '0 28px', fontSize: '16px', whiteSpace: 'nowrap' }} onClick={handleStart}>
              {t.createRoomNow}
            </Button>
          </NameForm>
        )}

        <CardRow>
          {cards.map(card => (
            <InfoCard key={card.title} surface={tok.bgSurface} border={tok.border}>
              <CardIcon>{card.icon}</CardIcon>
              <CardTitle color={tok.text}>{card.title}</CardTitle>
              <CardDesc color={tok.textMuted}>{card.desc}</CardDesc>
            </InfoCard>
          ))}
          <AdCard href="https://wa.me/5548992111825" target="_blank" rel="noopener noreferrer">
            <CardIcon>📣</CardIcon>
            <CardTitle color="#fff">{t.adTitle}</CardTitle>
            <CardDesc color="rgba(255,255,255,0.85)">{t.adDesc}</CardDesc>
          </AdCard>
          <DonateCard onClick={() => setDonateOpen(true)}>
            <CardIcon>☕</CardIcon>
            <CardTitle color="#fff">{t.donateCardTitle}</CardTitle>
            <CardDesc color="rgba(255,255,255,0.9)">{t.donateCardDesc}</CardDesc>
          </DonateCard>
        </CardRow>
      </Hero>

      <WhatsAppFloat href="https://wa.me/5548992111825" target="_blank" rel="noopener noreferrer" title={t.adTooltip}>
        💬
      </WhatsAppFloat>

      {donateOpen && <DonateModal tokens={tok} onClose={() => setDonateOpen(false)} />}
    </Wrapper>
  );
};
